import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  type CasoListItem,
  type NotificacionesResumen,
  type Page,
} from "@/lib/api";
import { ESTADO_CASO_PILL } from "@/lib/estadoCaso";
import { useAuth } from "@/lib/auth";
import { imeiCoincide } from "@/lib/imei";
import { textoCoincide } from "@/lib/busqueda";
import AyudaVista from "@/ui/AyudaVista";
import ActividadPill from "@/ui/ActividadPill";
import ImeisLista from "@/components/casos/ImeisLista";
import { ayudaPanel } from "@/components/ayuda/vistas/panel";
import Modal from "@/ui/Modal";
import Skeleton from "@/ui/Skeleton";
import Toggle from "@/ui/Toggle";

// Qué es cada tarjeta lo explica la Ayuda del Panel
// (`components/ayuda/vistas/panel.tsx`); antes era un tooltip por tarjeta.
type Stat = {
  label: string;
  value: number | string;
  tone: string;
};

// Cuántos IMEIs se muestran en la fila antes de colapsar el resto en "+N más".
const MAX_IMEIS_VISIBLES = 5;


export default function Dashboard() {
  const user = useAuth((s) => s.user);
  const [activos, setActivos] = useState<CasoListItem[]>([]);
  const [totalAbiertos, setTotalAbiertos] = useState(0);
  const [totalCerrados, setTotalCerrados] = useState(0);
  const [notif, setNotif] = useState<NotificacionesResumen | null>(null);
  const [loading, setLoading] = useState(true);

  // Filtros del dashboard (cliente-side sobre la lista cargada)
  const [filtroModelo, setFiltroModelo] = useState<string>("todos");
  const [filtroCliente, setFiltroCliente] = useState<string>("todos");
  // Filtro por asignado: "todos" | "sin_asignar" | "<nombre>"
  const [filtroAsignado, setFiltroAsignado] = useState<string>("todos");
  const [agruparPorCliente, setAgruparPorCliente] = useState(false);
  // En mobile los filtros (selects + incluir cerrados) viven en un sheet.
  const [filtrosOpen, setFiltrosOpen] = useState(false);
  // Grupos (por cliente) expandidos cuando "Agrupar por cliente" está on.
  // Por defecto colapsados: ves la lista de clientes y abres solo los que quieres.
  const [gruposExpandidos, setGruposExpandidos] = useState<Set<string>>(new Set());

  // Buscador por IMEI/folio/cliente/TE/modelo. Sobre los activos es client-side (instantáneo);
  // si se marca "incluir cerrados" se dispara además una búsqueda server-side.
  const [busqueda, setBusqueda] = useState("");
  const [incluirCerrados, setIncluirCerrados] = useState(false);
  const [cerradosMatch, setCerradosMatch] = useState<CasoListItem[]>([]);
  const [buscandoCerrados, setBuscandoCerrados] = useState(false);

  // Tickets con respuesta del proveedor sin contestar. Va en el Panel porque
  // es el vistazo de la mañana; el contador de la barra es el que persigue.
  const [prov, setProv] = useState<{ total: number; dias_mas_viejo: number } | null>(null);
  useEffect(() => {
    void api
      .get<{ total: number; dias_mas_viejo: number }>("/api/casos/proveedor-pendiente?limite=1")
      .then(setProv)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const empty: Page<CasoListItem> = { items: [], total: 0, page: 1, page_size: 0 };
    Promise.all([
      api.get<Page<CasoListItem>>("/api/casos?abiertos=true&page_size=200").catch(() => empty),
      api.get<Page<CasoListItem>>("/api/casos?abiertos=false&page_size=1").catch(() => empty),
      api.get<NotificacionesResumen>("/api/notificaciones").catch(
        () => ({ total_no_leidas: 0, items: [] }),
      ),
    ]).then(([abiertos, cerrados, n]) => {
      setActivos(abiertos.items);
      setTotalAbiertos(abiertos.total);
      setTotalCerrados(cerrados.total);
      setNotif(n);
      setLoading(false);
    });
  }, []);

  // Búsqueda server-side en casos CERRADOS cuando se marca "incluir cerrados".
  // Debounce 300ms. Los activos se filtran al instante client-side.
  useEffect(() => {
    const q = busqueda.trim();
    if (!incluirCerrados || !q) {
      setCerradosMatch([]);
      setBuscandoCerrados(false);
      return;
    }
    let cancelado = false;
    setBuscandoCerrados(true);
    const t = setTimeout(() => {
      api
        .get<Page<CasoListItem>>(
          `/api/casos?abiertos=false&q=${encodeURIComponent(q)}&page_size=50`,
        )
        .then((r) => {
          if (!cancelado) setCerradosMatch(r.items);
        })
        .catch(() => {
          if (!cancelado) setCerradosMatch([]);
        })
        .finally(() => {
          if (!cancelado) setBuscandoCerrados(false);
        });
    }, 300);
    return () => {
      cancelado = true;
      clearTimeout(t);
    };
  }, [busqueda, incluirCerrados]);

  const modelosDisponibles = useMemo(() => {
    const set = new Set<string>();
    for (const c of activos) {
      if (c.modelo) set.add(c.modelo);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [activos]);

  const clientesDisponibles = useMemo(() => {
    const set = new Set<string>();
    for (const c of activos) {
      if (c.cliente_nombre) set.add(c.cliente_nombre);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [activos]);

  const { asignadosDisponibles, haySinAsignar } = useMemo(() => {
    const set = new Set<string>();
    let huerfanos = false;
    for (const c of activos) {
      if (c.asignado_a_nombre) set.add(c.asignado_a_nombre);
      else huerfanos = true;
    }
    return {
      asignadosDisponibles: Array.from(set).sort((a, b) => a.localeCompare(b)),
      haySinAsignar: huerfanos,
    };
  }, [activos]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim();
    return activos.filter((c) => {
      if (filtroModelo !== "todos" && c.modelo !== filtroModelo) return false;
      if (filtroCliente !== "todos" && c.cliente_nombre !== filtroCliente) return false;
      if (filtroAsignado === "sin_asignar") {
        if (c.asignado_a_nombre) return false;
      } else if (filtroAsignado !== "todos") {
        if (c.asignado_a_nombre !== filtroAsignado) return false;
      }
      if (q) {
        // Espejo (hasta donde alcanza la fila del listado) de lo que busca el
        // servidor en Casos: folio, cliente, TE, marca/modelo y ticket del
        // proveedor, sin acentos. Antes solo IMEI+folio, y con «incluir
        // cerrados» prendido los cerrados SÍ matcheaban por cliente (van por
        // el servidor) y los activos no — dos resultados distintos en la
        // misma caja.
        const matchImei = c.imeis.some((i) => imeiCoincide(q, i.imei));
        const matchTexto = textoCoincide(
          q,
          c.folio,
          c.cliente_nombre,
          c.ticket_externo,
          c.marca,
          c.modelo,
          c.jimi_work_order_no,
          c.asignado_a_nombre,
        );
        if (!matchImei && !matchTexto) return false;
      }
      return true;
    });
  }, [activos, filtroModelo, filtroCliente, filtroAsignado, busqueda]);

  const grupos = useMemo(() => {
    if (!agruparPorCliente) return null;
    const map = new Map<string, CasoListItem[]>();
    for (const c of filtrados) {
      const k = c.cliente_nombre || "(sin cliente)";
      const arr = map.get(k);
      if (arr) arr.push(c);
      else map.set(k, [c]);
    }
    return Array.from(map.entries()).sort(
      (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
    );
  }, [filtrados, agruparPorCliente]);

  function toggleGrupo(cliente: string) {
    setGruposExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(cliente)) next.delete(cliente);
      else next.add(cliente);
      return next;
    });
  }
  const todosExpandidos =
    !!grupos && grupos.length > 0 && grupos.every(([c]) => gruposExpandidos.has(c));
  function toggleTodosGrupos() {
    if (!grupos) return;
    setGruposExpandidos(todosExpandidos ? new Set() : new Set(grupos.map(([c]) => c)));
  }

  function limpiarFiltros() {
    setFiltroModelo("todos");
    setFiltroCliente("todos");
    setFiltroAsignado("todos");
    setBusqueda("");
    setIncluirCerrados(false);
  }
  const algunFiltroActivo =
    filtroModelo !== "todos" ||
    filtroCliente !== "todos" ||
    filtroAsignado !== "todos" ||
    busqueda.trim() !== "" ||
    incluirCerrados;
  // Filtros "de campo" (los que viven en el sheet en mobile), para el badge
  // del botón Filtros.
  const nFiltrosCampo =
    (filtroCliente !== "todos" ? 1 : 0) +
    (filtroAsignado !== "todos" ? 1 : 0) +
    (filtroModelo !== "todos" ? 1 : 0) +
    (incluirCerrados ? 1 : 0);

  // Controles de filtro (incluir cerrados + 3 selects). Se reusan inline en
  // desktop y apilados dentro del sheet en mobile.
  const filtrosCampos = (
    <>
      <Toggle
        checked={incluirCerrados}
        onChange={setIncluirCerrados}
        label="Incluir cerrados"
      />
      <select
        className="input text-xs py-1 w-full md:w-[200px]"
        value={filtroCliente}
        onChange={(e) => setFiltroCliente(e.target.value)}
        disabled={loading}
      >
        <option value="todos">Todos los clientes</option>
        {clientesDisponibles.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
      <select
        className="input text-xs py-1 w-full md:w-[200px]"
        value={filtroAsignado}
        onChange={(e) => setFiltroAsignado(e.target.value)}
        disabled={loading}
      >
        <option value="todos">Todos los asignados</option>
        {haySinAsignar && <option value="sin_asignar">— Sin asignar —</option>}
        {asignadosDisponibles.map((u) => (
          <option key={u} value={u}>{u}</option>
        ))}
      </select>
      <select
        className="input text-xs py-1 w-full md:w-[200px]"
        value={filtroModelo}
        onChange={(e) => setFiltroModelo(e.target.value)}
        disabled={loading}
      >
        <option value="todos">Todos los modelos</option>
        {modelosDisponibles.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
    </>
  );

  const stats: Stat[] = [
    {
      label: "Abiertos",
      value: totalAbiertos,
      tone: "amber",
    },
    {
      label: "Alertas",
      value: notif?.total_no_leidas ?? 0,
      tone: "red",
    },
    {
      label: "Cerrados",
      value: totalCerrados,
      tone: "emerald",
    },
    {
      label: "Total",
      value: totalAbiertos + totalCerrados,
      tone: "sky",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">Panel principal</h1>
            <AyudaVista titulo="Panel principal" {...ayudaPanel()} />
          </div>
          <p className="text-sm text-zinc-400">Hola, {user?.nombre}.</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s) => (
            <StatCard key={s.label} {...s} />
          ))}
        </div>
        <div className="text-right">
          <Link to="/estadisticas" className="text-xs text-accent-400 hover:underline">
            Ver estadísticas de soporte →
          </Link>
        </div>
      </div>

      {prov !== null && prov.total > 0 && (
        <Link
          to="/proveedor"
          className={`card block transition hover:border-accent-500/40 ${
            prov.dias_mas_viejo >= 5 ? "border-red-500/40" : ""
          }`}
        >
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span
              className={`text-2xl font-semibold ${
                prov.dias_mas_viejo >= 5 ? "text-red-300" : "text-accent-300"
              }`}
            >
              {prov.total}
            </span>
            <span className="text-sm text-zinc-300">
              ticket{prov.total === 1 ? "" : "s"} donde <strong>el proveedor ya contestó</strong> y
              no le hemos respondido
            </span>
            <span className="text-xs text-zinc-500 w-full sm:w-auto">
              el más viejo lleva {prov.dias_mas_viejo} día{prov.dias_mas_viejo === 1 ? "" : "s"} ·
              ver la lista →
            </span>
          </div>
        </Link>
      )}

      <div className="card space-y-3">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-sm font-medium text-zinc-300">
                Casos activos{" "}
                <span className="text-xs text-zinc-500 ml-1">
                  ({filtrados.length}
                  {algunFiltroActivo && filtrados.length !== activos.length
                    ? ` de ${activos.length}`
                    : ""}
                  )
                </span>
              </h2>
              <Toggle
                checked={agruparPorCliente}
                onChange={setAgruparPorCliente}
                label="Agrupar por cliente"
              />
            </div>

            {/* Filtros inline en desktop */}
            <div className="hidden md:flex items-center gap-2 flex-wrap">
              <input
                type="search"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="🔎 IMEI, folio, cliente, TE…"
                className="input text-xs py-1 w-[230px]"
                disabled={loading}
                aria-label="Buscar por IMEI, folio, cliente, TE, modelo o ticket del proveedor"
              />
              {filtrosCampos}
              {algunFiltroActivo && (
                <button onClick={limpiarFiltros} className="btn-ghost text-xs">
                  Limpiar
                </button>
              )}
            </div>
          </div>

          {/* Mobile: buscador full-width + botón Filtros */}
          <div className="flex items-center gap-2 md:hidden">
            <input
              type="search"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="🔎 IMEI, folio, cliente, TE…"
              className="input text-sm flex-1 min-w-0"
              disabled={loading}
              aria-label="Buscar por IMEI, folio, cliente, TE, modelo o ticket del proveedor"
            />
            <button
              type="button"
              onClick={() => setFiltrosOpen(true)}
              className="btn-ghost text-sm shrink-0 border border-ink-600"
            >
              Filtros{nFiltrosCampo > 0 ? ` (${nFiltrosCampo})` : ""}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : activos.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No hay casos activos. Crea uno desde la pestaña Casos.
          </p>
        ) : filtrados.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Ningún caso activo coincide con los filtros.
          </p>
        ) : agruparPorCliente && grupos ? (
          <div className="space-y-1">
            <div className="flex justify-end">
              <button onClick={toggleTodosGrupos} className="btn-ghost text-xs">
                {todosExpandidos ? "Colapsar todo" : "Expandir todo"}
              </button>
            </div>
            {grupos.map(([cliente, casos]) => {
              const abierto = gruposExpandidos.has(cliente);
              return (
                <div key={cliente} className="border-b border-ink-700/60 last:border-0">
                  <button
                    type="button"
                    onClick={() => toggleGrupo(cliente)}
                    aria-expanded={abierto}
                    className="w-full flex items-center gap-2 py-2 px-2 -mx-2 text-left rounded hover:bg-ink-800/40"
                  >
                    <span className="w-3 shrink-0 text-xs text-zinc-500">{abierto ? "▾" : "▸"}</span>
                    <h3 className="flex-1 break-words line-clamp-2 text-xs uppercase tracking-wide text-accent-400 font-medium">
                      {cliente}
                    </h3>
                    <span className="shrink-0 text-xs text-zinc-500">
                      ({casos.length} caso{casos.length === 1 ? "" : "s"})
                    </span>
                  </button>
                  {abierto && (
                    <ul className="divide-y divide-ink-700/40 pb-2">
                      {casos.map((c) => (
                        <CasoRow key={c.id} caso={c} ocultarCliente />
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <ul className="divide-y divide-ink-700">
            {filtrados.map((c) => (
              <CasoRow key={c.id} caso={c} />
            ))}
          </ul>
        )}

        {incluirCerrados && busqueda.trim() && (
          <div className="border-t border-ink-700 pt-3">
            <h3 className="text-xs uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-2">
              Casos cerrados que coinciden
              {buscandoCerrados && (
                <span className="text-zinc-600 normal-case tracking-normal">buscando…</span>
              )}
            </h3>
            {!buscandoCerrados && cerradosMatch.length === 0 ? (
              <p className="text-sm text-zinc-600">Ningún caso cerrado coincide.</p>
            ) : (
              <ul className="divide-y divide-ink-700/60 opacity-90">
                {cerradosMatch.map((c) => (
                  <CasoRow key={`cerrado-${c.id}`} caso={c} />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>


      {/* Sheet de filtros (mobile) */}
      <Modal
        open={filtrosOpen}
        onOpenChange={setFiltrosOpen}
        title="Filtros"
        closeOnOutsideClick
        footer={
          <>
            {algunFiltroActivo && (
              <button
                onClick={() => {
                  limpiarFiltros();
                }}
                className="btn-ghost text-sm mr-auto"
              >
                Limpiar
              </button>
            )}
            <button onClick={() => setFiltrosOpen(false)} className="btn-primary text-sm">
              Listo
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">{filtrosCampos}</div>
      </Modal>
    </div>
  );
}

function CasoRow({
  caso,
  ocultarCliente,
}: {
  caso: CasoListItem;
  ocultarCliente?: boolean;
}) {
  return (
    <li className="py-1.5">
      <Link
        to={`/casos/${caso.folio}`}
        className="block hover:bg-ink-800/40 rounded px-2 -mx-2 py-1"
      >
        {/* Mobile: card compacta */}
        <div className="xl:hidden space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-accent-400 text-sm">{caso.folio}</span>
            <span
              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ESTADO_CASO_PILL[caso.estado]}`}
            >
              {caso.estado}
            </span>
            <ActividadPill dias={caso.dias_sin_actividad} nivel={caso.nivel_actividad} />
          </div>
          {!ocultarCliente && (
            <div className="text-sm text-zinc-200 break-words">{caso.cliente_nombre || "—"}</div>
          )}
          <div className="text-xs text-zinc-400 break-words">
            {caso.marca || "-"} {caso.modelo || ""}
          </div>
          <div className="text-xs text-zinc-400 break-words">
            {caso.asignado_a_nombre ? `👤 ${caso.asignado_a_nombre}` : "Sin asignar"}
          </div>
        </div>

        {/* Desktop: fila en grid */}
        <div className="hidden xl:grid grid-cols-12 gap-3 items-center text-sm">
          <span className="col-span-3 font-mono text-accent-400 truncate">{caso.folio}</span>
          {!ocultarCliente && (
            <span className="col-span-2 text-zinc-200 break-words line-clamp-2">
              {caso.cliente_nombre || "—"}
            </span>
          )}
          <span
            className={`${ocultarCliente ? "col-span-5" : "col-span-3"} text-zinc-300 break-words line-clamp-2`}
          >
            {caso.marca || "-"} {caso.modelo || ""}
          </span>
          {/* Sin line-clamp: el nombre completo se ve (antes iba cortado y
              entero solo en un tooltip). */}
          <span className="col-span-2 text-xs text-zinc-400 break-words">
            {caso.asignado_a_nombre ? (
              <>
                <span className="text-zinc-500">👤 </span>
                {caso.asignado_a_nombre}
              </>
            ) : (
              <span className="text-zinc-600 italic">Sin asignar</span>
            )}
          </span>
          <span className="col-span-2 text-right">
            <span className="inline-flex items-center gap-1">
              <ActividadPill dias={caso.dias_sin_actividad} nivel={caso.nivel_actividad} />
              <span
                className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ESTADO_CASO_PILL[caso.estado]}`}
              >
                {caso.estado}
              </span>
            </span>
          </span>
        </div>

      </Link>
      {/* IMEIs (mobile y desktop) FUERA del enlace: el «+N más» es un botón
          que despliega el resto (antes vivía en un tooltip), y un botón
          dentro de un <a> rompe la navegación. */}
      {caso.imeis.length > 0 && (
        <div className="mt-0.5 flex items-start gap-1">
          <span className="mr-0.5 pt-0.5 text-[10px] text-zinc-500" aria-hidden>
            📟
          </span>
          <ImeisLista imeis={caso.imeis} max={MAX_IMEIS_VISIBLES} />
        </div>
      )}
    </li>
  );
}

function StatCard({ label, value, tone }: Stat) {
  const cls: Record<string, string> = {
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    red: "bg-red-500/10 text-red-300 border-red-500/30",
    emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    sky: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  };
  return (
    <div className={`rounded-xl border ${cls[tone]} p-3 md:p-4`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-mono mt-1">{value}</div>
    </div>
  );
}
